import React, { Suspense, lazy } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import InstallPrompt from './components/InstallPrompt';
import { AuthProvider } from './context/AuthContext';
import { SettingsProvider } from './context/SettingsContext';
import { CartProvider } from './context/CartContext';
import { WatchlistProvider } from './context/WatchlistContext';
import { GarageProvider } from './context/GarageContext';

// Public pages: home is eager, the rest lazy-load for faster first paint.
import Home from './pages/Home';
const Inventory = lazy(() => import('./pages/Inventory'));
const VehicleDetail = lazy(() => import('./pages/VehicleDetail'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const Services = lazy(() => import('./pages/Services'));
const Parts = lazy(() => import('./pages/Parts'));
const PartDetail = lazy(() => import('./pages/PartDetail'));
const Notices = lazy(() => import('./pages/Notices'));
const NoticeDetail = lazy(() => import('./pages/NoticeDetail'));
const Login = lazy(() => import('./pages/Login'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const OrderSuccess = lazy(() => import('./pages/OrderSuccess'));
const Cart = lazy(() => import('./pages/Cart'));
const TrackOrder = lazy(() => import('./pages/TrackOrder'));
const Account = lazy(() => import('./pages/Account'));
const Memberships = lazy(() => import('./pages/Memberships'));
const Locations = lazy(() => import('./pages/Locations'));
const LocationLanding = lazy(() => import('./pages/LocationLanding'));
const BlogList = lazy(() => import('./pages/Blog').then(m => ({ default: m.BlogList })));
const BlogDetail = lazy(() => import('./pages/Blog').then(m => ({ default: m.BlogDetail })));
const AdminMarketing = lazy(() => import('./pages/admin/AdminMarketing'));

// Admin code is heavy → always lazy so it never ships in the customer bundle.
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminVehicles = lazy(() => import('./pages/admin/AdminVehicles'));
const AdminVehicleForm = lazy(() => import('./pages/admin/AdminVehicleForm'));
const AdminParts = lazy(() => import('./pages/admin/AdminParts'));
const AdminPartForm = lazy(() => import('./pages/admin/AdminPartForm'));
const AdminNotices = lazy(() => import('./pages/admin/AdminNotices'));
const AdminNoticeForm = lazy(() => import('./pages/admin/AdminNoticeForm'));
const AdminAbout = lazy(() => import('./pages/admin/AdminAbout'));
const AdminLeads = lazy(() => import('./pages/admin/AdminLeads'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));
const AdminAnalytics = lazy(() => import('./pages/admin/AdminAnalytics'));
const AdminOrders = lazy(() => import('./pages/admin/AdminOrders'));
const AdminCustomers = lazy(() => import('./pages/admin/AdminCustomers'));

import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import ProtectedRoute from './components/ProtectedRoute';
import NoticeBanner from './components/NoticeBanner';
import AnnouncementPopup from './components/AnnouncementPopup';
import LiveVisitors from './components/LiveVisitors';
import LandingGate from './components/LandingGate';
import PublicGate from './components/PublicGate';
import useAnalytics from './hooks/useAnalytics';

function RouteFallback() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="w-12 h-12 border-4 border-[#e11d2c] border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  useAnalytics();
  if (location.hash?.includes('session_id=')) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <AuthCallback />
      </Suspense>
    );
  }
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<LandingGate />} />
        <Route path="/inventory" element={<PublicGate requireFlag="publish_inventory"><Inventory /></PublicGate>} />
        <Route path="/vehicle/:id" element={<PublicGate requireFlag="publish_inventory"><VehicleDetail /></PublicGate>} />
        <Route path="/parts" element={<PublicGate requireFlag="publish_parts"><Parts /></PublicGate>} />
        <Route path="/parts/:id" element={<PublicGate requireFlag="publish_parts"><PartDetail /></PublicGate>} />
        <Route path="/notices" element={<PublicGate requireFlag="publish_notices"><Notices /></PublicGate>} />
        <Route path="/notices/:id" element={<PublicGate requireFlag="publish_notices"><NoticeDetail /></PublicGate>} />
        <Route path="/about" element={<PublicGate><About /></PublicGate>} />
        <Route path="/contact" element={<PublicGate><Contact /></PublicGate>} />
        <Route path="/services" element={<PublicGate><Services /></PublicGate>} />
        <Route path="/login" element={<Login />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/order/success" element={<OrderSuccess />} />
        <Route path="/cart" element={<PublicGate requireFlag="publish_parts"><Cart /></PublicGate>} />
        <Route path="/track-order" element={<TrackOrder />} />
        <Route path="/memberships" element={<PublicGate><Memberships /></PublicGate>} />
        <Route path="/locations" element={<PublicGate><Locations /></PublicGate>} />
        <Route path="/used-cars-in/:city" element={<PublicGate><LocationLanding /></PublicGate>} />
        <Route path="/blog" element={<PublicGate><BlogList /></PublicGate>} />
        <Route path="/blog/:slug" element={<PublicGate><BlogDetail /></PublicGate>} />
        <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute requireAdmin><AdminDashboard /></ProtectedRoute>} />
        <Route path="/admin/orders" element={<ProtectedRoute requireAdmin><AdminOrders /></ProtectedRoute>} />
        <Route path="/admin/orders/:id" element={<ProtectedRoute requireAdmin><AdminOrders /></ProtectedRoute>} />
        <Route path="/admin/customers" element={<ProtectedRoute requireAdmin><AdminCustomers /></ProtectedRoute>} />
        <Route path="/admin/vehicles" element={<ProtectedRoute requireAdmin><AdminVehicles /></ProtectedRoute>} />
        <Route path="/admin/vehicles/new" element={<ProtectedRoute requireAdmin><AdminVehicleForm /></ProtectedRoute>} />
        <Route path="/admin/vehicles/:id/edit" element={<ProtectedRoute requireAdmin><AdminVehicleForm /></ProtectedRoute>} />
        <Route path="/admin/parts" element={<ProtectedRoute requireAdmin><AdminParts /></ProtectedRoute>} />
        <Route path="/admin/parts/new" element={<ProtectedRoute requireAdmin><AdminPartForm /></ProtectedRoute>} />
        <Route path="/admin/parts/:id/edit" element={<ProtectedRoute requireAdmin><AdminPartForm /></ProtectedRoute>} />
        <Route path="/admin/notices" element={<ProtectedRoute requireAdmin><AdminNotices /></ProtectedRoute>} />
        <Route path="/admin/notices/new" element={<ProtectedRoute requireAdmin><AdminNoticeForm /></ProtectedRoute>} />
        <Route path="/admin/notices/:id/edit" element={<ProtectedRoute requireAdmin><AdminNoticeForm /></ProtectedRoute>} />
        <Route path="/admin/about" element={<ProtectedRoute requireAdmin><AdminAbout /></ProtectedRoute>} />
        <Route path="/admin/leads" element={<ProtectedRoute requireAdmin><AdminLeads /></ProtectedRoute>} />
        <Route path="/admin/settings" element={<ProtectedRoute requireAdmin><AdminSettings /></ProtectedRoute>} />
        <Route path="/admin/analytics" element={<ProtectedRoute requireAdmin><AdminAnalytics /></ProtectedRoute>} />
        <Route path="/admin/marketing" element={<ProtectedRoute requireAdmin><AdminMarketing /></ProtectedRoute>} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <SettingsProvider>
            <CartProvider>
              <WatchlistProvider>
              <GarageProvider>
              <ScrollToTop />
              <NoticeBanner />
              <Header />
              <main>
                <AppRouter />
              </main>
              <Footer />
              <InstallPrompt />
              <AnnouncementPopup />
              <div className="fixed bottom-4 left-4 z-40 print:hidden pointer-events-none">
                <LiveVisitors />
              </div>
              <Toaster position="bottom-right" />
              </GarageProvider>
              </WatchlistProvider>
            </CartProvider>
          </SettingsProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
